// src/components/layout/Header.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Header.module.css';
import { getMediaUrl, userApi, authApi } from '../../api';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from '../common/LanguageSwitcher';
import logo from '../../assets/logo_1.png';

function Header({ loggedIn, username, setLoggedIn, userId, roleLevel = 4 }) {
  const { t } = useTranslation();
  const isAdmin = roleLevel <= 1;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [avatarUrl, setAvatarUrl] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
  }, []);

  // Avatar-URL aus dem eigenen Profil laden (avatar_media_key statt geraten)
  useEffect(() => {
    let cancelled = false;
    if (loggedIn && userId) {
      userApi.getProfile().then(result => {
        if (cancelled) return;
        if (result.success && result.data?.avatar_has && result.data?.avatar_media_key) {
          setAvatarUrl(getMediaUrl(result.data.avatar_media_key));
        } else {
          setAvatarUrl(null);
        }
      });
    } else {
      setAvatarUrl(null);
    }
    return () => { cancelled = true; };
  }, [loggedIn, userId]);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  };

  const handleLogout = () => {
    setLoggedIn(false);
    setDropdownOpen(false);
    authApi.logout();
  };

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className={styles.navbar}>
      <Link to="/" className={styles.logoSection}>
        <img src={logo} alt="MangaShelf" className={styles.logoSvg} />
        <span className={styles.siteName}>
          Manga<span className={styles.siteNameAccent}>Shelf</span>
        </span>
      </Link>

      {/* Desktop Navigation */}
      <div className={styles.centerNav}>
        <nav className={styles.navbarLinksLeft}>
          <NavLink to="/mein-regal" className={({ isActive }) => isActive ? `${styles.navLink} ${styles.activeLink}` : styles.navLink}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="3" width="4" height="12" stroke="currentColor" strokeWidth="1.5"/><rect x="8" y="3" width="4" height="12" stroke="currentColor" strokeWidth="1.5"/><rect x="13" y="3" width="4" height="12" stroke="currentColor" strokeWidth="1.5"/></svg>
            {t('nav.myShelf')}
          </NavLink>
          <NavLink to="/wishlist" className={({ isActive }) => isActive ? `${styles.navLink} ${styles.activeLink}` : styles.navLink}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 3L10.5 7.5H15L11.5 10.5L13 15L9 12L5 15L6.5 10.5L3 7.5H7.5L9 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
            {t('nav.wishlist')}
          </NavLink>
        </nav>
        <NavLink to="/add-volume" className={({ isActive }) => `${styles.addVolumeButton} ${isActive ? styles.addVolumeActive : ''}`}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          <span>{t('nav.addVolume')}</span>
        </NavLink>
        <nav className={styles.navbarLinksRight}>
          <NavLink to="/discover" className={({ isActive }) => isActive ? `${styles.navLink} ${styles.activeLink}` : styles.navLink}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/><path d="M13 13L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            {t('nav.discover')}
          </NavLink>
          <NavLink to="/friends" className={({ isActive }) => isActive ? `${styles.navLink} ${styles.activeLink}` : styles.navLink}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="6" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/><circle cx="13" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M1 15C1 12.2386 3.23858 10 6 10C8.76142 10 11 12.2386 11 15" stroke="currentColor" strokeWidth="1.5"/><path d="M11 15C11 13.3431 12.3431 12 14 12C15.6569 12 17 13.3431 17 15" stroke="currentColor" strokeWidth="1.5"/></svg>
            {t('nav.friends')}
          </NavLink>
        </nav>
      </div>

      {/* Rechte Seite */}
      <div className={styles.rightSection}>
        {loggedIn && <NotificationBell />}
        <LanguageSwitcher className={styles.langSwitcher} />
        <button className={styles.themeToggle} onClick={toggleTheme} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
          {theme === 'dark' ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="4" stroke="currentColor" strokeWidth="1.5"/><path d="M10 2V4M10 16V18M18 10H16M4 10H2M15.5 4.5L14 6M6 14L4.5 15.5M15.5 15.5L14 14M6 6L4.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/></svg>
          )}
        </button>

        {loggedIn && (
          <div className={styles.profileButton}>
            <button className={styles.userButton} onClick={toggleDropdown}>
              <div className={styles.avatar}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt={username} className={styles.avatarImage}
                    onError={(e) => { e.target.style.display='none'; e.target.parentElement.textContent=username.charAt(0).toUpperCase(); }}
                  />
                ) : username.charAt(0).toUpperCase()}
              </div>
              <span className={styles.username}>{username}</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className={dropdownOpen ? styles.chevronOpen : ''}>
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {dropdownOpen && (
              <div className={styles.dropdown} ref={dropdownRef}>
                <Link to="/profile" className={styles.dropdownItem} onClick={() => setDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/><path d="M2 14C2 11.2386 4.23858 9 7 9H9C11.7614 9 14 11.2386 14 14" stroke="currentColor" strokeWidth="1.5"/></svg>
                  {t('nav.profile')}
                </Link>
                <Link to="/settings" className={styles.dropdownItem} onClick={() => setDropdownOpen(false)}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  {t('nav.settings')}
                </Link>
                {isAdmin && (
                  <Link to="/admin" className={`${styles.dropdownItem} ${styles.dropdownItemAdmin}`} onClick={() => setDropdownOpen(false)}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 1L10 5.5H15L11 8.5L12.5 13L8 10L3.5 13L5 8.5L1 5.5H6L8 1Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>
                    {t('nav.adminPanel')}
                  </Link>
                )}
                <div className={styles.dropdownDivider}></div>
                <div className={`${styles.dropdownItem} ${styles.dropdownItemLogout}`} onClick={handleLogout}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 14H3C2.44772 14 2 13.5523 2 13V3C2 2.44772 2.44772 2 3 2H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M11 11L14 8L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                  {t('nav.logout')}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Hamburger für Mobile */}
        <button
          className={styles.hamburger}
          onClick={() => setMobileMenuOpen(prev => !prev)}
          aria-label="Menü"
        >
          {mobileMenuOpen ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M3 6h18M3 12h18M3 18h18"/></svg>
          )}
        </button>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className={styles.mobileMenu} onClick={() => setMobileMenuOpen(false)}>
          <NavLink to="/mein-regal" className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileLinkActive : ''}`}>
            <svg width="20" height="20" viewBox="0 0 18 18" fill="none"><rect x="3" y="3" width="4" height="12" stroke="currentColor" strokeWidth="1.5"/><rect x="8" y="3" width="4" height="12" stroke="currentColor" strokeWidth="1.5"/><rect x="13" y="3" width="4" height="12" stroke="currentColor" strokeWidth="1.5"/></svg>
            {t('nav.myShelf')}
          </NavLink>
          <NavLink to="/wishlist" className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileLinkActive : ''}`}>
            <svg width="20" height="20" viewBox="0 0 18 18" fill="none"><path d="M9 3L10.5 7.5H15L11.5 10.5L13 15L9 12L5 15L6.5 10.5L3 7.5H7.5L9 3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/></svg>
            {t('nav.wishlist')}
          </NavLink>
          <NavLink to="/add-volume" className={({ isActive }) => `${styles.mobileLink} ${styles.mobileLinkAccent} ${isActive ? styles.mobileLinkActive : ''}`}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            {t('nav.addVolume')}
          </NavLink>
          <NavLink to="/discover" className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileLinkActive : ''}`}>
            <svg width="20" height="20" viewBox="0 0 18 18" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/><path d="M13 13L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            {t('nav.discover')}
          </NavLink>
          <NavLink to="/friends" className={({ isActive }) => `${styles.mobileLink} ${isActive ? styles.mobileLinkActive : ''}`}>
            <svg width="20" height="20" viewBox="0 0 18 18" fill="none"><circle cx="6" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/><circle cx="13" cy="7" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M1 15C1 12.2386 3.23858 10 6 10C8.76142 10 11 12.2386 11 15" stroke="currentColor" strokeWidth="1.5"/><path d="M11 15C11 13.3431 12.3431 12 14 12C15.6569 12 17 13.3431 17 15" stroke="currentColor" strokeWidth="1.5"/></svg>
            {t('nav.friends')}
          </NavLink>
        </div>
      )}
    </header>
  );
}

export default Header;
